import React from 'react';
import { Box, Typography, Paper, TextField, Button, Alert } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

type SendResult = {
  ok: boolean;
  totalTokens: number;
  successCount: number;
  failureCount: number;
  removed: number;
};

export default function PushNotificationsManager() {
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [link, setLink] = React.useState('/');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<SendResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleSend = async () => {
    setBusy(true);
    setError(null);
    setResult(null);

    try {
      const callable = httpsCallable(functions, 'sendBroadcastNotification');
      const res = await callable({ title: title.trim(), body: body.trim(), link: link.trim() || '/' });
      setResult(res.data as SendResult);
    } catch (e: any) {
      // firebase/functions callable errors commonly look like:
      // { code: 'functions/internal', message: 'internal', details: {...} }
      const code = e?.code ? String(e.code) : '';
      const message = e?.message ? String(e.message) : 'Failed to send notification';
      const details = e?.details ? JSON.stringify(e.details) : '';

      // Include non-enumerable fields like stack/name for debugging
      let raw = '';
      try {
        raw = JSON.stringify(e, Object.getOwnPropertyNames(e));
      } catch {
        raw = String(e);
      }

      const hint =
        code === 'functions/internal' && message.toLowerCase() === 'internal'
          ? 'Hint: This usually means the Cloud Function is not deployed/reachable, or it crashed. Deploy Functions first (firebase login, then cd functions && npm run deploy) and try again.'
          : '';

      const composed = [
        code ? `Code: ${code}` : null,
        message ? `Message: ${message}` : null,
        details ? `Details: ${details}` : null,
        raw ? `Raw: ${raw}` : null,
        hint ? hint : null,
      ]
        .filter(Boolean)
        .join('\n');

      console.error('Push send error:', e);
      setError(composed || 'Failed to send notification');
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || title.trim().length === 0 || body.trim().length === 0;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4, px: 2 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 900,
          mx: 'auto',
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          border: '3px solid #d4af37',
          boxShadow: '0 20px 60px rgba(212, 175, 55, 0.18)',
          background: 'linear-gradient(135deg, #fff 0%, #fafaf8 100%)',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5, color: '#2c3e50' }}>
          Push Notifications
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Send a broadcast notification to all devices that clicked “Enable Notifications”.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Sent. Tokens: {result.totalTokens}, Success: {result.successCount}, Failed: {result.failureCount}, Removed invalid: {result.removed}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 120 }}
            fullWidth
          />
          <TextField
            label="Link (optional)"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            helperText="Example: /donate/online or full https:// link"
            fullWidth
          />
          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            inputProps={{ maxLength: 300 }}
            multiline
            minRows={3}
            fullWidth
            sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
          />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSend}
            disabled={disabled}
            sx={{ bgcolor: '#d4af37', color: '#000', fontWeight: 800, px: 3, '&:hover': { bgcolor: '#c19b2f' } }}
          >
            {busy ? 'Sending…' : 'Send Notification'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
